<script lang="ts">
  import "../app.css"

  import posthog from "posthog-js"
  import { browser } from "$app/environment"
  import { onMount } from "svelte"
  import { page, navigating } from "$app/stores"
  import { expoOut } from "svelte/easing"
  import { slide } from "svelte/transition"
  import { Toaster } from "svelte-sonner"

  interface LayoutData {
    session?: {
      user?: {
        email?: string
        id?: string
      }
    }
    user?: {
      email?: string
      id?: string
    }
  }

  interface Props {
    children?: import("svelte").Snippet
    data?: LayoutData
  }

  let { children, data }: Props = $props()

  const useDefaultMeta =
    ($page?.route?.id?.includes("marketing") ||
      $page?.route?.id?.includes("admin")) &&
    !$page?.route?.id?.includes("giveaway")

  // Bento tracking state
  let bentoLoaded = $state(false)
  let lastTrackedRoute = $state("")

  onMount(() => {
    if (browser) {
      // Identify user on Crisp Chat
      globalThis?.$crisp?.push([
        "set",
        "user:email",
        data?.session?.user?.email,
      ])

      globalThis?.$crisp?.push(["safe", true])
      // console.log("🔍 Root layout mounted on route:", $page.url.pathname)

      posthog.init("phc_k0ZdGCKgnPX4dBlJOBTgzZdjSSJDpyS78tQ9t0sez59", {
        api_host: "https://us.i.posthog.com",
        person_profiles: "identified_only", // or 'always' to create profiles for anonymous users as well
      })

      // Load Bento script
      loadBentoScript()
    }
  })

  // Reactive statement to track when session changes or route changes
  $effect(() => {
    const currentRoute = $page.url.pathname
    // console.log(
    //   "📍 Route changed to:",
    //   currentRoute,
    //   "bentoLoaded:",
    //   bentoLoaded,
    //   "lastTracked:",
    //   lastTrackedRoute,
    // )

    if (browser && bentoLoaded && lastTrackedRoute !== currentRoute) {
      // console.log("✅ Calling trackBentoView for route:", currentRoute)
      trackBentoView()
    }
  })

  function loadBentoScript() {
    // console.log("🔄 Loading Bento script...")

    // Check if script is already loaded
    if (
      typeof window !== "undefined" &&
      (window as unknown as { bento$?: any }).bento$
    ) {
      // console.log("✅ Bento already loaded")
      bentoLoaded = true
      return
    }

    const script = document.createElement("script")
    script.src =
      "https://app.bentonow.com/83c6f111ca59b1c446a7ffc5af50ddec.js?js=true"
    script.async = true
    script.defer = true

    script.onload = () => {
      // console.log("✅ Bento script loaded successfully")
      bentoLoaded = true
      // Track current page when script loads if we haven't tracked it yet
      if (lastTrackedRoute !== $page.url.pathname) {
        trackBentoView()
      }
    }

    script.onerror = (error) => {
      console.error("❌ Failed to load Bento script:", error)
    }

    document.head.appendChild(script)
  }

  function trackBentoView() {
    // console.log("🎯 trackBentoView called for route:", $page.url.pathname)

    try {
      if (typeof window !== "undefined" && window.bento$) {
        // console.log("📊 Bento$ found, executing callback...")

        window.bento$(() => {
          const user = data?.session?.user || data?.user
          // console.log("👤 User data for Bento:", user)

          // Identify user if they have an email
          if (user && user.email) {
            // console.log("🔍 Identifying user:", user.email)
            window?.bento?.identify(user.email)
            window?.bento.updateFields({
              name: user?.user_metadata?.full_name,
              user_id: user.id,
            })
          }

          // Always call view
          // console.log("👁️ Calling Bento view() for route:", $page.url.pathname)
          window?.bento?.view()
          window?.bento?.hideChat()

          // Mark current route as tracked to prevent multiple calls for same route
          lastTrackedRoute = $page.url.pathname
          // console.log("✅ Route tracked successfully:", lastTrackedRoute)
        })
      } else {
        console.warn("⚠️ Bento$ not available on window object")
      }
    } catch (error) {
      console.error("❌ Error tracking Bento view:", error)
    }
  }
</script>

<svelte:head>
  {#if useDefaultMeta}
    <title>Promo Amp - Run Engaging Giveaways, Sweepstakes & Competitions</title
    >
    <meta
      property="og:description"
      content="Promo Amp is the ultimate tool for running giveaways, sweepstakes, and competitions that drive real value for your business. Join thousands of brands leveraging our platform to create viral campaigns."
    />
    <meta property="og:image" content="https://img.promoamp.com/og_image.png" />
  {/if}
</svelte:head>

{#if $navigating}
  <!-- 
    Loading animation for next page since svelte doesn't show any indicator. 
     - delay 100ms because most page loads are instant, and we don't want to flash 
     - long 12s duration because we don't actually know how long it will take
     - exponential easing so fast loads (>100ms and <1s) still see enough progress,
       while slow networks see it moving for a full 12 seconds
  -->
  <div
    class="fixed w-full top-0 right-0 left-0 h-1 z-50 bg-primary"
    in:slide={{ delay: 50, duration: 12000, axis: "x", easing: expoOut }}
  ></div>
{/if}
<Toaster richColors />
{@render children?.()}
